import { get } from 'lodash';
import {
  ELEMENT_DICTIONARY,
  OPTION_DICTIONARY,
  stripQuotes,
} from 'src/pages/facesheet/_id/eligibility/surveyjsTemplates/createElAssessTemplate';

export const getEligibleServiceUtilV5 = (
  screening,
  identifiedNeeds,
  { updatedCookingSuppliesEligibilityEnabled = false } = {},
) => {
  try {
    if (!screening) { return null; }

    const eligibleServices = [];

    const screen = screening.data.screen;
    const qVerifyMedicaidEnrollment = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qVerifyMedicaidEnrollment,
    );
    const qVerifyIdentity = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qVerifyIdentity,
    );
    const qConfirmAccurateInfo = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qConfirmAccurateInfo,
    );
    const qNYExcludedPopulation = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qNYExcludedPopulation,
    );
    const qEnhancedPopulations = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qEnhancedPopulations,
    );
    const qClinicalCriteria = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qClinicalCriteria,
    );
    const qDifficultyWalking = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qDifficultyWalking,
    );
    const qDifficultyDressing = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qDifficultyDressing,
    );

    const qRefrigerationBreastMilk = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qRefrigerationBreastMilk,
    );
    const qRefrigerationMedication = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qRefrigerationMedication,
    );
    const qTransportationTroubleReasons = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qTransportationTroubleReasons,
    );
    const qTypesOfActivitiesTransportation = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qTypesOfActivitiesTransportation,
    );
    const qSelectScreening = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qSelectScreening,
    );

    const hasAccurateInfo = stripQuotes(
      get(qConfirmAccurateInfo, 'answer.value'),
    ) === OPTION_DICTIONARY.oMemberDoesAttest;
    const hasVerifiedIdentity = stripQuotes(
      get(qVerifyIdentity, 'answer.value'),
    ) === OPTION_DICTIONARY.oVerifiedMemberIdentity;

    const VERIFIED_MEDICAID_ANSWERS = {
      // // Option available before April 8
      GENERAL_VERIFIED: 'I was able to verify the member is currently enrolled in Medicaid.',
      // // Options available after April 8
      MANAGED_CARE_VERIFIED: 'I was able to verify the member is currently enrolled in Medicaid Managed Care',
      FFS_VERIFIED: 'I was able to verify the member is currently enrolled in Medicaid Fee-for-Service',
      NOT_VERIFIED: 'I was not able to verify the member is currently enrolled in Medicaid',
    };

    const verifyMedicaidAnswer = get(qVerifyMedicaidEnrollment, 'answer.value');
    const hasVerifiedMedicaid = verifyMedicaidAnswer === VERIFIED_MEDICAID_ANSWERS.GENERAL_VERIFIED ||
      verifyMedicaidAnswer === VERIFIED_MEDICAID_ANSWERS.MANAGED_CARE_VERIFIED;

    const hasVerifiedMedicaidManagedCare = verifyMedicaidAnswer === VERIFIED_MEDICAID_ANSWERS.MANAGED_CARE_VERIFIED;
    const hasVerifiedMedicaidFeeForService = verifyMedicaidAnswer === VERIFIED_MEDICAID_ANSWERS.FFS_VERIFIED;

    const isMemberInExcludedPopulation = get(qNYExcludedPopulation, 'answer.value') === 'Yes';
    const hasValidScreening = Boolean(get(qSelectScreening, 'answer.value'));

    const hasHousingNeed = Boolean(identifiedNeeds.includes('housing'));

    const enhancedPopulations = get(qEnhancedPopulations, 'answers', [])
      .map((a) => stripQuotes(a.value));
    const clinicalCriteria = get(qClinicalCriteria, 'answers', [])
      .map((a) => stripQuotes(a.value));
    const qHousingSafetyConcerns = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qHousingSafetyConcerns,
    );

    const hasDifficultyWalking = get(qDifficultyWalking, 'answer.value') === 'Yes';
    const hasDifficultyDressing = get(qDifficultyDressing, 'answer.value') === 'Yes';
    const requireRefrigerationBreastMilk = get(qRefrigerationBreastMilk, 'answer.value') === 'Yes';
    const requireRefrigerationMedication = get(qRefrigerationMedication, 'answer.value') === 'Yes';
    const qCareRecoveryNeed = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qCareRecoveryNeed,
    );
    const hasCareRecoveryNeed = get(qCareRecoveryNeed, 'answer.value') === 'Yes';
    const qHospitalRisk = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qHospitalRisk,
    );
    const qServiceDuplicationCheck = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qServiceDuplicationCheck,
    );
    const listOfServiceDuplication = get(qServiceDuplicationCheck, 'answers', [])
      .map((a) => stripQuotes(a.value));
    const hasHospitalRisk = get(qHospitalRisk, 'answer.value') === 'Yes';
    const transportationTroubleReasons = get(qTransportationTroubleReasons, 'answers', [])
      .map((a) => stripQuotes(a.value));
    const typesOfActivitiesTransportation = get(qTypesOfActivitiesTransportation, 'answers', [])
      .map((a) => stripQuotes(a.value));

    const qUtilitiesIssues = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qUtilitiesIssues,
    );
    const hasUtilitiesIssues = get(qUtilitiesIssues, 'answer.value') === 'Yes';
    // Get all snomed codes selected
    const snomedCodesSelected = [];

    const optionIdToSnomedCodeMap = {};
    // const snomedCodesAvailable = [];  // for debugging purposes
    screen.questions.forEach((q) => {
      if (!q.options) { return; }
      q.options.forEach((o) => {
        if (o.snomed) {
          optionIdToSnomedCodeMap[o.id] = o.snomed;
          // for debugging purposes
          // snomedCodesAvailable.push({
          //   question: q.primary_text,
          //   option_value: o.value,
          //   snomed: o.snomed,
          // });
        }
      });
    });

    screen.questions.forEach((q) => {
      if (!q.answer && !q.answers) { return; }
      if (q.answer) {
        const snomedCode = optionIdToSnomedCodeMap[q.answer.option_id];
        if (snomedCode) {
          snomedCodesSelected.push(snomedCode);
        }
      }
      if (q.answers) {
        q.answers.forEach((a) => {
          const snomedCode = optionIdToSnomedCodeMap[a.option_id];
          if (snomedCode) {
            snomedCodesSelected.push(snomedCode);
          }
        });
      }
    });
    const qHousingConcerns = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qHousingConcerns,
    );
    const qHousingIssues = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qHousingIssues,
    );
    const listOfHousingConcerns = get(qHousingConcerns, 'answers', [])
      .map((a) => stripQuotes(a.value));
    const listOfHousingIssues = get(qHousingIssues, 'answers', [])
      .map((a) => stripQuotes(a.value));
    const qUtilitiesIssuesTypes = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qUtilitiesIssuesTypes,
    );
    const listOfUtilitiesIssues = get(qUtilitiesIssuesTypes, 'answers', [])
      .map((a) => stripQuotes(a.value));

    const qEmploymentSituation = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qEmploymentSituation,
    );
    const employmentSituationHasAnswer = Boolean(get(qEmploymentSituation, 'answer.value'));
    const qFoodAssistanceType = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qFoodAssistanceType,
    );
    const qSchoolTrainingHelp = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qSchoolTrainingHelp,
    );
    const schoolTrainingHelpHasAnswer = Boolean(get(qSchoolTrainingHelp, 'answer.value'));
    const listOfFoodAssistance = get(qFoodAssistanceType, 'answers', [])
      .map((a) => stripQuotes(a.value));
    const qCanPrepareMeals = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qCanPrepareMeals,
    );
    const canPrepareMeals = get(qCanPrepareMeals, 'answer.value') === 'Yes';
    const cannotPrepareMeals = get(qCanPrepareMeals, 'answer.value') === 'No';

    const qCaregiverPrepareMeals = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qCaregiverPrepareMeals,
    );
    const caregiverCannotPrepareMeals = get(qCaregiverPrepareMeals, 'answer.value') === 'No';
    const caregiverCanPrepareMeals = get(qCaregiverPrepareMeals, 'answer.value') === 'Yes';

    const qTransportationAccess = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qTransportationAccess,
    );
    const hasTransportationAccess = get(qTransportationAccess, 'answer.value') === 'Yes';

    const qLivingSituation = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qLivingSituation,
    );
    const qLivingSituationAnswer = stripQuotes(get(qLivingSituation, 'answer.value'));
    const havePlaceToLiveWithProblems = qLivingSituationAnswer ===
      OPTION_DICTIONARY.oPlaceToLiveWithProblems;
    const livingSituationHasAnswer = Boolean(get(qLivingSituation, 'answer.value'));
    // Check if the client is eligible for services
    if (
      !hasAccurateInfo ||
      !hasVerifiedIdentity ||
      (
        !hasVerifiedMedicaid && !hasVerifiedMedicaidFeeForService
      ) ||
      !hasValidScreening ||
      isMemberInExcludedPopulation
    ) {
      return [];
    }

    const hasHousingSafetyConcerns = get(qHousingSafetyConcerns, 'answer.value') === 'Yes';

    const enhancedConditionsSetOne = enhancedPopulations.includes(OPTION_DICTIONARY.medicaidHighUtilizer) ||
      enhancedPopulations.includes(OPTION_DICTIONARY.enrolledNYS) ||
      enhancedPopulations.includes(OPTION_DICTIONARY.pregnantAndPostpartum) ||
      enhancedPopulations.includes(OPTION_DICTIONARY.highRiskChildUnderEighteen) ||
      enhancedPopulations.includes(OPTION_DICTIONARY.individualsChronicReleased) ||
      enhancedPopulations.includes(OPTION_DICTIONARY.childNutrition) ||
      enhancedPopulations.includes(OPTION_DICTIONARY.otherPopulations) ||
      enhancedPopulations.includes(OPTION_DICTIONARY.idd);

    const housingIssuesSet = listOfHousingIssues.includes(OPTION_DICTIONARY.oStayingWithFamilyOrFriends) ||
        listOfHousingIssues.includes(OPTION_DICTIONARY.oStayingInShelter) ||
        listOfHousingIssues.includes(OPTION_DICTIONARY.oStayingNotLiving) ||
        listOfHousingIssues.includes(OPTION_DICTIONARY.oStayingNotLivingSafe) ||
        listOfHousingIssues.includes(OPTION_DICTIONARY.oMightLoseHousing) ||
        listOfHousingIssues.includes(OPTION_DICTIONARY.oHousingCostsTooMuch) ||
        listOfHousingIssues.includes(OPTION_DICTIONARY.oHousingWorriedAboutEviction);

    const housingIssuesSetTwo = listOfHousingIssues.includes(OPTION_DICTIONARY.oStayingWithFamilyOrFriends) ||
        listOfHousingIssues.includes(OPTION_DICTIONARY.oStayingInShelter) ||
        listOfHousingIssues.includes(OPTION_DICTIONARY.oStayingNotLiving) ||
        listOfHousingIssues.includes(OPTION_DICTIONARY.oStayingNotLivingSafe) ||
        listOfHousingIssues.includes(OPTION_DICTIONARY.oMightLoseHousing) ||
        listOfHousingIssues.includes(OPTION_DICTIONARY.oBehindOnRent) ||
        listOfHousingIssues.includes(OPTION_DICTIONARY.oHousingCostsTooMuch) ||
        listOfHousingIssues.includes(OPTION_DICTIONARY.oHousingWorriedAboutEviction);

    const serviceDuplicationSet =
      listOfServiceDuplication.includes(OPTION_DICTIONARY.oDuplicationNutritionalCounseling) ||
      listOfServiceDuplication.includes(OPTION_DICTIONARY.oMedicallyTailoredMeals) ||
      listOfServiceDuplication.includes(OPTION_DICTIONARY.oHealthyHomes) ||
      listOfServiceDuplication.includes(OPTION_DICTIONARY.oDuplicationDevelopmentalDisabilities) ||
      listOfServiceDuplication.includes(OPTION_DICTIONARY.oDuplicationTraumaticBrainInjury) ||
      listOfServiceDuplication.includes(OPTION_DICTIONARY.oDuplicationNursingHomeTransition) ||
      listOfServiceDuplication.includes(OPTION_DICTIONARY.oDuplicationChildrenWaiver) ||
      listOfServiceDuplication.length === 0;

    const serviceDuplicationSetTwo =
      listOfServiceDuplication.includes(OPTION_DICTIONARY.oDuplicationNutritionalCounseling) ||
      listOfServiceDuplication.includes(OPTION_DICTIONARY.oHealthyHomes) ||
      listOfServiceDuplication.includes(OPTION_DICTIONARY.oDuplicationDevelopmentalDisabilities) ||
      listOfServiceDuplication.includes(OPTION_DICTIONARY.oDuplicationTraumaticBrainInjury) ||
      listOfServiceDuplication.includes(OPTION_DICTIONARY.oDuplicationChildrenWaiver) ||
      listOfServiceDuplication.length === 0;

    const serviceDuplicationSetThree =
      listOfServiceDuplication.includes(OPTION_DICTIONARY.oMedicallyTailoredMeals) ||
      listOfServiceDuplication.includes(OPTION_DICTIONARY.oHealthyHomes) ||
      listOfServiceDuplication.includes(OPTION_DICTIONARY.oDuplicationDevelopmentalDisabilities) ||
      listOfServiceDuplication.includes(OPTION_DICTIONARY.oDuplicationTraumaticBrainInjury) ||
      listOfServiceDuplication.includes(OPTION_DICTIONARY.oDuplicationChildrenWaiver) ||
      listOfServiceDuplication.length === 0;

    const qAdditionalClinicalConditions = screen.questions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qAdditionalClinicalConditions,
    );
    const additionalClinicalConditions = get(qAdditionalClinicalConditions, 'answers', [])
      .map((a) => stripQuotes(a.value));
    // Navigation Services (Level 1)
    if (
      (
        hasVerifiedMedicaidFeeForService ||
        hasVerifiedMedicaidManagedCare
      ) && enhancedPopulations.length === 0 &&
      (
        transportationTroubleReasons.length > 0 ||
        typesOfActivitiesTransportation.length > 0 ||
        listOfFoodAssistance.length > 0 ||
        livingSituationHasAnswer ||
        listOfHousingIssues.length > 0 ||
        listOfHousingConcerns.length > 0 ||
        listOfUtilitiesIssues.length > 0 ||
        employmentSituationHasAnswer ||
        schoolTrainingHelpHasAnswer
      )
    ) {
      eligibleServices.push('Navigation Services (Level 1)');
    }

    // Enhanced Care Management Level 2
    if (
      hasVerifiedMedicaidManagedCare &&
      enhancedPopulations.length > 0 &&
      (
        transportationTroubleReasons.length > 0 ||
        typesOfActivitiesTransportation.length > 0 ||
        listOfFoodAssistance.length > 0 ||
        livingSituationHasAnswer ||
        listOfHousingIssues.length > 0 ||
        listOfHousingConcerns.length > 0 ||
        listOfUtilitiesIssues.length > 0 ||
        employmentSituationHasAnswer ||
        schoolTrainingHelpHasAnswer
      )
    ) {
      eligibleServices.push('Enhanced Care Management (Level 2)');
    }

    // Home Accessibility / Safety Modification
    if (
      hasHousingNeed &&
      enhancedConditionsSetOne &&
      havePlaceToLiveWithProblems &&
      (
        hasDifficultyWalking ||
        hasDifficultyDressing
      ) &&
      (
      additionalClinicalConditions.includes(OPTION_DICTIONARY.qDisabilityIndependence)
      )
    ) {
      eligibleServices.push('Home Accessibility / Safety Modification (Housing)');
    }

    // Home Remediation
    if (
      hasHousingNeed &&
      enhancedConditionsSetOne &&
      hasHousingSafetyConcerns &&
      (
        listOfHousingConcerns.includes('Mold or mildew is present') ||
        listOfHousingConcerns.includes('Bugs or rodents like mice or rats')
      ) &&
      additionalClinicalConditions.includes(OPTION_DICTIONARY.qHealthConditionEnvironment)
    ) {
      eligibleServices.push('Home Remediation Assistance: Mold and Pest (Housing)');
    }

    // Home Remediation Assistance: Ventilation Improving Systems
    if (
      enhancedConditionsSetOne &&
      hasHousingNeed &&
      hasHousingSafetyConcerns &&
      listOfHousingConcerns.includes(OPTION_DICTIONARY.oNoHeatingOrCooling) &&
      (
        clinicalCriteria.includes(OPTION_DICTIONARY.chronicCondition) ||
        clinicalCriteria.includes(OPTION_DICTIONARY.heatRelatedIllness) ||
        clinicalCriteria.includes(OPTION_DICTIONARY.coldRelatedIllness) ||
        clinicalCriteria.includes(OPTION_DICTIONARY.atRiskThermo)
      )
    ) {
      eligibleServices.push('Home Remediation Assistance: Ventilation Improving Systems (Housing)');
    }

    if (
      enhancedConditionsSetOne &&
      (
        (
          requireRefrigerationMedication &&
          additionalClinicalConditions.includes(OPTION_DICTIONARY.qRefrigeratedMedication)
        ) ||
        (
          requireRefrigerationBreastMilk &&
          (
            additionalClinicalConditions.includes(OPTION_DICTIONARY.qRefrigerationBreastMilk) ||
            clinicalCriteria.includes(OPTION_DICTIONARY.enternalOrParenteral)
          )
        )
      ) &&
      (
        livingSituationHasAnswer ||
        listOfHousingIssues.length > 0 ||
        listOfHousingConcerns.length > 0 ||
        listOfFoodAssistance.length > 0
      )
    ) {
      eligibleServices.push('Home Remediation Assistance: Equipment provision (refrigeration units) (Housing)');
    }

    const asthmaLivingConcerns = qLivingSituationAnswer === OPTION_DICTIONARY.oPlaceToLiveWithProblems ||
      qLivingSituationAnswer === OPTION_DICTIONARY.oNoPlaceToLive;
    const asthmaHousingConcerns = listOfHousingConcerns.includes(OPTION_DICTIONARY.oKitchenInadequate) ||
      listOfHousingConcerns.includes(OPTION_DICTIONARY.oMoldOrMildew) ||
      listOfHousingConcerns.includes(OPTION_DICTIONARY.oNoHeatingOrCooling) ||
      listOfHousingConcerns.includes(OPTION_DICTIONARY.oBugsOrRodents) ||
      listOfHousingConcerns.includes(OPTION_DICTIONARY.oPossibleLeadPaint) ||
      listOfHousingConcerns.includes(OPTION_DICTIONARY.oSmokeDetectors) ||
      listOfHousingConcerns.includes(OPTION_DICTIONARY.oWaterLeaks);
    if (
      enhancedConditionsSetOne &&
      (asthmaLivingConcerns || asthmaHousingConcerns || hasUtilitiesIssues) &&
      clinicalCriteria.includes(OPTION_DICTIONARY.asthma)
    ) {
      eligibleServices.push('Asthma Remediation (Housing)');
    }

    if (
      enhancedConditionsSetOne &&
      (
        listOfHousingIssues.includes(OPTION_DICTIONARY.oStayingWithFamilyOrFriends) ||
        listOfHousingIssues.includes(OPTION_DICTIONARY.oStayingInShelter) ||
        listOfHousingIssues.includes(OPTION_DICTIONARY.oStayingNotLiving) ||
        listOfHousingIssues.includes(OPTION_DICTIONARY.oStayingNotLivingSafe) ||
        listOfHousingIssues.includes(OPTION_DICTIONARY.oMightLoseHousing)
      ) &&
      (
        hasCareRecoveryNeed ||
        hasHospitalRisk
      ) &&
      (
        additionalClinicalConditions.includes(OPTION_DICTIONARY.qPreSurgicalCare) ||
        additionalClinicalConditions.includes(OPTION_DICTIONARY.qAcuteCareHospitalization) ||
        additionalClinicalConditions.includes(OPTION_DICTIONARY.qRecuperationCare) ||
        additionalClinicalConditions.includes(OPTION_DICTIONARY.qMedicaidServiceRisk)
      )
    ) {
      eligibleServices.push('Medical Respite - Preprocedural Care (Housing)');
      eligibleServices.push('Medical Respite - Post Hospitalization Care (Housing)');
    }

    if (
      enhancedConditionsSetOne &&
      housingIssuesSetTwo
    ) {
      eligibleServices.push('Rent/Temporary Housing Rent Payment Assistance (Housing)');
    }

    if (
      enhancedConditionsSetOne &&
      housingIssuesSetTwo &&
      hasHousingSafetyConcerns &&
      (
        listOfHousingConcerns.includes(OPTION_DICTIONARY.oNoHeatingOrCooling) ||
        listOfHousingConcerns.includes(OPTION_DICTIONARY.oWaterLeaks)
      ) &&
      hasUtilitiesIssues &&
      (
        listOfUtilitiesIssues.includes(OPTION_DICTIONARY.oElectricityShutoff) ||
          listOfUtilitiesIssues.includes(OPTION_DICTIONARY.oNoRunningWater) ||
          listOfUtilitiesIssues.includes(OPTION_DICTIONARY.oNoElectricityAfford) ||
          listOfUtilitiesIssues.includes(OPTION_DICTIONARY.oNoHeatAfford) ||
          listOfUtilitiesIssues.includes(OPTION_DICTIONARY.oNoACAfford)
      )
    ) {
      eligibleServices.push(
        'Utility Payment Assistance (must be eligible for and receiving Rent /' +
        'Temporary Rental Assistance to qualify) (Utilities)',
      );
      eligibleServices.push(
        'Utility Activation Payment (must be eligible for and receiving Rent /' +
        'Temporary Rental Assistance to qualify) (Utilities)',
      );
      eligibleServices.push(
        'Utility Arrears Payment (must be eligible for and receiving Rent /' +
        'Temporary Rental Assistance to qualify) (Utilities)',
      );
    }

    if (
      enhancedConditionsSetOne &&
      housingIssuesSet
    ) {
      eligibleServices.push('Pre-tenancy Services');
    }

    if (
      enhancedConditionsSetOne &&
      housingIssuesSet &&
      (
        hasHousingSafetyConcerns ||
        hasUtilitiesIssues
      )
    ) {
      eligibleServices.push('Community Transitional Supports (Housing)');
      eligibleServices.push('Tenancy Sustaining Services (Housing)');
      eligibleServices.push('Housing Transition and Navigation Services (Housing)');
    }

    if (
      enhancedConditionsSetOne &&
      listOfFoodAssistance.includes(OPTION_DICTIONARY.oNutritionalCounseling) &&
      serviceDuplicationSetThree
    ) {
      eligibleServices.push('Nutritional Counseling and Education (Food)');
    }

    if (
      (
        (
          enhancedConditionsSetOne && clinicalCriteria.includes(OPTION_DICTIONARY.chronicCondition)
        ) || enhancedPopulations.includes(OPTION_DICTIONARY.pregnantAndPostpartum)
      ) &&
      (
        cannotPrepareMeals || caregiverCannotPrepareMeals
      ) &&
      serviceDuplicationSetTwo
    ) {
      eligibleServices.push('Medically Tailored Meals (MTM) (Food)');
    }

    if (
      enhancedConditionsSetOne &&
      listOfFoodAssistance.includes('Home-delivered meals') &&
      (
        cannotPrepareMeals || caregiverCannotPrepareMeals
      ) &&
      serviceDuplicationSetTwo
    ) {
      eligibleServices.push('Clinically Appropriate Meals (Food)');
    }

    if (
      enhancedConditionsSetOne &&
      (
        listOfFoodAssistance.includes(OPTION_DICTIONARY.oFoodPantryAccess) ||
        listOfFoodAssistance.includes(OPTION_DICTIONARY.oGroceryShoppingHelp)
      ) &&
      canPrepareMeals &&
      caregiverCanPrepareMeals &&
      serviceDuplicationSet
    ) {
      eligibleServices.push('Food Prescriptions (Voucher / Boxes) (Food)');
    }

    if (
      (
        enhancedPopulations.includes(OPTION_DICTIONARY.pregnantAndPostpartum) ||
        enhancedPopulations.includes(OPTION_DICTIONARY.highRiskChildUnderEighteen) ||
        enhancedPopulations.includes(OPTION_DICTIONARY.childNutrition)
      ) &&
      (
        listOfFoodAssistance.includes(OPTION_DICTIONARY.oFoodPantryAccess) ||
        listOfFoodAssistance.includes(OPTION_DICTIONARY.oGroceryShoppingHelp)
      ) &&
      canPrepareMeals &&
      caregiverCanPrepareMeals &&
      serviceDuplicationSet
    ) {
      eligibleServices.push('Fresh Produce and Nonperishable Groceries - Pantry Stocking (Food)');
    }

    if (
      enhancedConditionsSetOne &&
      listOfFoodAssistance.includes(OPTION_DICTIONARY.oCookingSupplies) &&
      (
        updatedCookingSuppliesEligibilityEnabled ||
        serviceDuplicationSet
      )
    ) {
      eligibleServices.push('Cooking Supplies');
    }

    if (
      enhancedConditionsSetOne &&
      hasTransportationAccess &&
      (
        transportationTroubleReasons.includes(OPTION_DICTIONARY.oNoDriversLicense) ||
        transportationTroubleReasons.includes(OPTION_DICTIONARY.oNoWorkingVehicle) ||
        transportationTroubleReasons.includes(OPTION_DICTIONARY.oCannotTravelAlone)
      )
    ) {
      eligibleServices.push('Private Transportation (must also have at least one other HRSN need) (Transportation)');
      eligibleServices.push('Public Transportation (must also have at least one other HRSN need) (Transportation)');
    }

    return eligibleServices;
  } catch (error) {
    window.datadogLogs.logger.error('Error getting eligible services', {
      error,
      errorMessage: error.message,
      screening,
      identifiedNeeds,
    });
    return [];
  }
};

export default getEligibleServiceUtilV5;
